import { ComponentDataDestService } from '../component-data-dest.service';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { EventParticipantDTO } from '../../../dto/event/event-participant.dto';
import { Observable, tap, catchError, of, timer, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

// Create a debounced update mechanism to batch multiple changes to the same participant
const updateQueue = new Map<string, {data: any, subject: Subject<any>}>();

export const update_event_participation = (
  service: ComponentDataDestService,
  componentDataSourceService: ComponentDataSourceService,
  data: any
) => {
  // Add console logs to track when this function is triggered
  
  // Note: 'not_participant' status can be updated individually through the UI
  // We'll only skip bulk operations when that feature is implemented
  
  // Create a unique key for this participant update
  const participantKey = `${data.eventGuid}-${data.user_uuid || data.user_id}`;
  
  // If there's already a pending update for this participant, merge the data
  if (updateQueue.has(participantKey)) {
    const existing = updateQueue.get(participantKey)!;
    // Merge the new data with existing data, new data takes precedence
    existing.data = { ...existing.data, ...data };
    // Push the new data to the subject to reset the debounce timer
    existing.subject.next(existing.data);
    return;
  }
  
  // Create a new subject for this participant
  const subject = new Subject<any>();
  updateQueue.set(participantKey, { data: { ...data }, subject });
  
  // Set up debounced processing
  subject.pipe(
    debounceTime(500), // Wait 500ms after the last change
    switchMap((latestData) => {
      
      // Check if this update includes maintenance minutes data
      const hasMaintenanceMinutes = latestData.maintenance_minutes !== undefined || 
                                   (latestData.module_maintenance_minutes && latestData.module_maintenance_minutes.length > 0);
      
      // Transform and validate the data
      const participantData: EventParticipantDTO = {
        user: {
          uuid: latestData.user_uuid || latestData.user_id.toString(),  // Prefer user_uuid if available, fallback to user_id
          first_name: '',     // Backend will fill this
          last_name: '',      // Backend will fill this
          user: null,
          roles: null,
          organizations_id: null,
          locations: null,
          status: null
        },
        role: latestData.role,
        status: latestData.status
      };

      // Add maintenance minutes data if present
      if (hasMaintenanceMinutes) {
        const maintenanceMinutes: { [key: string]: number } = {};
        
        // Convert module_maintenance_minutes array to maintenance_minutes object
        if (latestData.module_maintenance_minutes && Array.isArray(latestData.module_maintenance_minutes)) {
          latestData.module_maintenance_minutes.forEach((moduleData: any) => {
            if (moduleData.module_id !== undefined && moduleData.minutes !== undefined) {
              maintenanceMinutes[moduleData.module_id.toString()] = moduleData.minutes;
            }
          });
        }
        
        // Add the data property to participantData
        (participantData as any).data = {
          maintenance_minutes: maintenanceMinutes
        };
      }

      return service.http.put<EventParticipantDTO>(
        `${environment.apiUrl}/events/${latestData.eventGuid}/participation`,
        {participant: participantData}
      ).pipe(
        tap(response => {
          // Remove from queue after successful update
          updateQueue.delete(participantKey);
          
          // Handle UI updates based on the type of change
          // Check if this is a status change (any status other than the current one)
          const isStatusChange = latestData.status !== undefined;
          
          if (hasMaintenanceMinutes && !isStatusChange) {
            // For maintenance minute updates without status change, don't refresh the UI to preserve the toggle state
            // The local UI has already been updated in the onChange handler
          } else {
            // Update data sources for role/status changes, including maintenance minutes when status changes
            componentDataSourceService.updateStoreData(of(response), 'event_participants').subscribe({
              error: (error) => console.error('[update_event_participation] Participants data source update error:', error)
            });

            componentDataSourceService.updateStoreData(of(response), 'event_instructors').subscribe({
              error: (error) => console.error('[update_event_participation] Instructors data source update error:', error)
            });
            
            componentDataSourceService.updateStoreData(of(response), 'event_waitlist').subscribe({
              error: (error) => console.error('[update_event_participation] Waitlist data source update error:', error)
            });

            componentDataSourceService.updateStoreData(of(response), 'event_cancellations').subscribe({
              error: (error) => console.error('[update_event_participation] Cancellations data source update error:', error)
            });
          }
        }),
        catchError(error => {
          console.error('[update_event_participation] API response ERROR:', error);
          console.error('[update_event_participation] Error details:', {
            status: error.status,
            statusText: error.statusText,
            error: error.error,
            url: error.url
          });
          if (error.error?.message) {
            console.error('[update_event_participation] Server error message:', error.error.message);
          }
          // Remove from queue even on error to prevent indefinite blocking
          updateQueue.delete(participantKey);
          throw error;
        })
      );
    })
  ).subscribe({
    next: (response) => {
      // UI updates are now handled in the tap operator above
    },
    error: (error) => console.error('[update_event_participation] Debounced subscription error:', error)
  });
  
  // Trigger the first emission
  subject.next(data);
}; 